import { supabase } from './supabase'

export interface Comment {
  id: string
  contribution_id: string
  user_id: string
  content: string
  created_at: string
  updated_at: string
  attachment_type?: 'contribution' | 'curation' | null
  attachment_id?: string | null
  user?: {
    id: string
    first_name: string
    last_name: string
    username: string
    avatar_url: string
  }
}

export const commentsService = {
  // Fetch all comments for a contribution
  async fetchComments(contributionId: string): Promise<Comment[]> {
    const { data, error } = await supabase
      .from('comments')
      .select(`
        *,
        user:users(
          id,
          first_name,
          last_name,
          username,
          avatar_url
        )
      `)
      .eq('contribution_id', contributionId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching comments:', error)
      throw error
    }

    return data || []
  },

  // Create a new comment
  async createComment(
    contributionId: string,
    userId: string,
    content: string,
    attachment?: { type: string; id: string } | null
  ): Promise<Comment> {
    const row: any = {
      contribution_id: contributionId,
      user_id: userId,
      content: content.trim()
    }
    if (attachment) {
      row.attachment_type = attachment.type
      row.attachment_id = attachment.id
    }

    const { data, error } = await supabase
      .from('comments')
      .insert(row)
      .select(`
        *,
        user:users(
          id,
          first_name,
          last_name,
          username,
          avatar_url
        )
      `)
      .single()

    if (error) {
      console.error('Error creating comment:', error)
      throw error
    }

    return data
  },

  // Delete a comment
  async deleteComment(commentId: string): Promise<void> {
    const { error } = await supabase
      .from('comments')
      .delete()
      .eq('id', commentId)

    if (error) {
      console.error('Error deleting comment:', error)
      throw error
    }
  },

  // Subscribe to new comments on a contribution
  subscribeToComments(contributionId: string, callback: (comment: Comment) => void) {
    const subscription = supabase
      .channel(`comments:${contributionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'comments',
          filter: `contribution_id=eq.${contributionId}`
        },
        async (payload) => {
          // Fetch the full comment with user data
          const { data } = await supabase
            .from('comments')
            .select(`
              *,
              user:users(
                id,
                first_name,
                last_name,
                username,
                avatar_url
              )
            `)
            .eq('id', payload.new.id)
            .single()

          if (data) {
            callback(data)
          }
        }
      )
      .subscribe()

    return subscription
  }
}
